import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import api, { fmtDT } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Table, TableHeader, TableRow, TableHead, TableBody, TableCell } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Search, X, UserCheck } from "lucide-react";
import VoiceInput from "@/components/VoiceInput";

export default function IPD() {
  const nav = useNavigate();
  const [sp] = useSearchParams();
  const [rows, setRows] = useState([]);
  const [open, setOpen] = useState(sp.get("new") === "1");
  const [doctors, setDoctors] = useState([]);
  const [beds, setBeds] = useState([]);

  // Smart patient picker for the admission dialog.
  const [pQuery, setPQuery] = useState("");
  const [pResults, setPResults] = useState([]);
  const [pLoading, setPLoading] = useState(false);
  const [picked, setPicked] = useState(null);   // full patient doc

  const [form, setForm] = useState({
    patient_id: "", doctor_id: "", bed_id: "", diagnosis: "",
    provisional_diagnosis: "", admission_type: "Routine", reason_for_admission: "",
    referring_doctor: "", attendant_name: "", attendant_relation: "", attendant_mobile: "",
    tpa_insurer: "", tpa_policy_no: "",
  });

  // React to shortcut re-navigation (Ctrl+I on any page).
  useEffect(() => {
    if (sp.get("new") === "1") setOpen(true);
  }, [sp]);

  const load = () => api.get("/ipd").then((r) => setRows(r.data));
  useEffect(() => {
    load();
    api.get("/doctors").then((r) => setDoctors(r.data));
    api.get("/beds", { params: { status: "available" } }).then((r) => setBeds(r.data));
  }, []);

  // Debounced smart search — UHID / Name / Mobile / OPD No / IPD No.
  const searchPatients = useCallback(async (raw) => {
    const q = (raw || "").trim();
    if (q.length < 2) { setPResults([]); return; }
    setPLoading(true);
    try {
      // 1) Direct patient search
      const [pat, opds, ipds] = await Promise.all([
        api.get("/patients", { params: { q, limit: 8 } }),
        // 2) OPD number → resolve to patient
        /^OPD/i.test(q)
          ? api.get("/opd").then(r => (r.data || []).filter(x => (x.opd_no || "").toLowerCase() === q.toLowerCase() || (x.opd_no || "").toLowerCase().includes(q.toLowerCase()))).catch(() => [])
          : Promise.resolve([]),
        // 3) IPD number → resolve to patient (existing IPD)
        /^IPD/i.test(q)
          ? api.get("/ipd").then(r => (r.data || []).filter(x => (x.ipd_no || "").toLowerCase() === q.toLowerCase() || (x.ipd_no || "").toLowerCase().includes(q.toLowerCase()))).catch(() => [])
          : Promise.resolve([]),
      ]);
      const seen = new Set();
      const out = [];
      for (const p of (pat.data || [])) { if (!seen.has(p.id)) { seen.add(p.id); out.push({ ...p, _via: "patient" }); } }
      // OPD hits → fetch the patient
      const opdList = Array.isArray(opds) ? opds : (opds?.data || []);
      for (const o of opdList.slice(0, 5)) {
        if (o.patient_id && !seen.has(o.patient_id)) {
          try {
            const { data: p } = await api.get(`/patients/${o.patient_id}`);
            if (p) { seen.add(p.id); out.push({ ...p, _via: `OPD ${o.opd_no}` }); }
          } catch (e) { console.warn("[IPD lookup skipped]", e?.message); }
        }
      }
      const ipdList = Array.isArray(ipds) ? ipds : (ipds?.data || []);
      for (const i of ipdList.slice(0, 5)) {
        if (i.patient_id && !seen.has(i.patient_id)) {
          try {
            const { data: p } = await api.get(`/patients/${i.patient_id}`);
            if (p) { seen.add(p.id); out.push({ ...p, _via: `IPD ${i.ipd_no}` }); }
          } catch (e) { console.warn("[IPD lookup skipped]", e?.message); }
        }
      }
      setPResults(out);
    } catch { toast.error("Search failed"); }
    finally { setPLoading(false); }
  }, []);

  useEffect(() => {
    const t = setTimeout(() => searchPatients(pQuery), 300);
    return () => clearTimeout(t);
  }, [pQuery, searchPatients]);

  const pickPatient = (p) => {
    setPicked(p);
    setForm((f) => ({ ...f, patient_id: p.id }));
    // Prefill attendant mobile with patient's if not already typed.
    if (!form.attendant_mobile && p.mobile) {
      // Intentionally left blank — attendant may differ from patient.
    }
    setPResults([]);
    setPQuery("");
  };
  const clearPatient = () => { setPicked(null); setForm({ ...form, patient_id: "" }); };

  // Preload when navigated with ?patient=<id>
  useEffect(() => {
    const pid = sp.get("patient") || "";
    if (pid) api.get(`/patients/${pid}`).then((r) => pickPatient(r.data)).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sp]);

  const create = async () => {
    try {
      const { data } = await api.post("/ipd", form);
      // Toast with a one-tap Print Stickers action right after admission.
      toast.success(`IPD ${data.ipd_no} admitted — tap dashboard button to print stickers`, {
        action: {
          label: "Print Stickers",
          onClick: () => window.open(`/ipd/${data.id}/stickers`, "_blank"),
        },
        duration: 6000,
      });
      setOpen(false); load(); nav(`/ipd/${data.id}`);
    } catch { toast.error("Failed"); }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div><h1 className="font-display text-2xl font-semibold">IPD Admissions</h1><p className="text-sm text-slate-500">Inpatient records and current admissions.</p></div>
        <Button onClick={() => setOpen(true)} data-testid="new-ipd-btn" className="bg-blue-700 hover:bg-blue-800 rounded-sm">+ New IPD Admission</Button>
      </div>
      <div className="bg-white border border-slate-200">
        <Table>
          <TableHeader><TableRow className="bg-slate-50">
            <TableHead className="label-tiny">IPD No</TableHead>
            <TableHead className="label-tiny">Patient</TableHead>
            <TableHead className="label-tiny">Doctor</TableHead>
            <TableHead className="label-tiny">Bed</TableHead>
            <TableHead className="label-tiny">Ward</TableHead>
            <TableHead className="label-tiny">Admission</TableHead>
            <TableHead className="label-tiny">Status</TableHead>
          </TableRow></TableHeader>
          <TableBody>
            {rows.map((o) => (
              <TableRow key={o.id} className="cursor-pointer hover:bg-blue-50" onClick={() => nav(`/ipd/${o.id}`)}>
                <TableCell className="font-mono text-xs">{o.ipd_no}</TableCell>
                <TableCell>{o.patient_name}</TableCell>
                <TableCell>{o.doctor_name}</TableCell>
                <TableCell className="font-mono text-xs">{o.bed_no || "-"}</TableCell>
                <TableCell><Badge variant="outline" className="rounded-sm">{o.ward || "-"}</Badge></TableCell>
                <TableCell className="text-xs">{fmtDT(o.admission_date)}</TableCell>
                <TableCell><Badge className={`rounded-sm ${o.status === "discharged" ? "bg-slate-100 text-slate-700" : "bg-blue-100 text-blue-800"}`}>{o.status}</Badge></TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>New IPD Admission</DialogTitle></DialogHeader>
          <div className="space-y-2">
            {/* Smart patient search — UHID / Name / Mobile / OPD / IPD number */}
            <label className="text-xs label-tiny">Patient</label>
            {picked ? (
              <div className="border-2 border-teal-300 bg-teal-50 rounded-sm px-3 py-2 flex items-center justify-between" data-testid="ipd-picked">
                <div>
                  <div className="font-semibold text-sm flex items-center gap-2">
                    <UserCheck className="w-4 h-4 text-teal-700"/> {picked.name}
                    <span className="text-[10px] font-mono text-slate-500">{picked.uhid}</span>
                  </div>
                  <div className="text-[11px] text-slate-600">
                    {picked.age ?? "-"} y · {picked.gender || "-"} · <span className="font-mono">{picked.mobile || "-"}</span>
                    {picked.address ? ` · ${picked.address}` : ""}
                  </div>
                </div>
                <button onClick={clearPatient} className="text-red-700 hover:bg-red-50 rounded-sm p-1" title="Change patient" data-testid="ipd-clear-patient">
                  <X className="w-4 h-4"/>
                </button>
              </div>
            ) : (
              <div>
                <div className="relative">
                  <Search className="w-3.5 h-3.5 absolute left-2 top-2.5 text-slate-400"/>
                  <Input
                    autoFocus
                    value={pQuery}
                    onChange={(e) => setPQuery(e.target.value)}
                    placeholder="Search by UHID · Name · Mobile · OPD/IPD No"
                    className="pl-7 h-9"
                    data-testid="ipd-patient-search"
                  />
                </div>
                {pLoading && <div className="text-[11px] text-slate-500 mt-1">Searching…</div>}
                {!pLoading && pResults.length > 0 && (
                  <div className="mt-1 border rounded-sm max-h-52 overflow-y-auto">
                    {pResults.map((p) => (
                      <button
                        key={p.id}
                        type="button"
                        onClick={() => pickPatient(p)}
                        className="w-full text-left px-2 py-1.5 hover:bg-blue-50 border-b last:border-b-0 text-sm"
                        data-testid={`ipd-patient-hit-${p.uhid}`}
                      >
                        <div className="font-medium flex items-center gap-2">
                          {p.name}
                          <span className="text-[10px] font-mono text-slate-500">{p.uhid}</span>
                          {p._via !== "patient" && (
                            <Badge variant="outline" className="text-[9px] h-4 px-1">{p._via}</Badge>
                          )}
                        </div>
                        <div className="text-[10px] text-slate-500">{p.age ?? "-"}/{p.gender || "-"} · {p.mobile || "-"}</div>
                      </button>
                    ))}
                  </div>
                )}
                {!pLoading && pQuery.trim().length >= 2 && pResults.length === 0 && (
                  <div className="mt-1 text-[11px] text-amber-700">No match — register the patient from OPD first.</div>
                )}
              </div>
            )}

            <label className="text-xs label-tiny">Doctor</label>
            <select className="w-full border border-slate-300 h-9" data-testid="ipd-doctor" value={form.doctor_id} onChange={(e) => setForm({ ...form, doctor_id: e.target.value })}>
              <option value="">-- Select --</option>
              {doctors.map((p) => <option key={p.id} value={p.id}>{p.name} ({p.department})</option>)}
            </select>
            <label className="text-xs label-tiny">Bed</label>
            <select className="w-full border border-slate-300 h-9" data-testid="ipd-bed" value={form.bed_id} onChange={(e) => setForm({ ...form, bed_id: e.target.value })}>
              <option value="">-- Select --</option>
              {beds.map((b) => <option key={b.id} value={b.id}>{b.bed_no} · {b.ward} · ₹{b.rate}</option>)}
            </select>
            <label className="text-xs label-tiny">Diagnosis</label>
            <VoiceInput as="input" testid="ipd-diagnosis" value={form.diagnosis} onChange={(v) => setForm({ ...form, diagnosis: v })} placeholder="Primary + secondary diagnoses…"/>

            {/* NABH §1 — Structured admission additions */}
            <div className="grid grid-cols-2 gap-2 pt-2 border-t mt-2">
              <div>
                <label className="text-xs label-tiny">Admission Type</label>
                <select className="w-full border border-slate-300 h-9 text-sm" value={form.admission_type} onChange={(e) => setForm({ ...form, admission_type: e.target.value })} data-testid="ipd-admtype">
                  <option>Routine</option><option>Emergency</option><option>MLC</option>
                  <option>Referral</option><option>Day-Care</option>
                </select>
              </div>
              <div>
                <label className="text-xs label-tiny">Provisional Diagnosis</label>
                <VoiceInput as="input" testid="ipd-provdx" value={form.provisional_diagnosis} onChange={(v) => setForm({ ...form, provisional_diagnosis: v })}/>
              </div>
              <div className="col-span-2">
                <label className="text-xs label-tiny">Reason for Admission</label>
                <VoiceInput as="input" testid="ipd-reason" value={form.reason_for_admission} onChange={(v) => setForm({ ...form, reason_for_admission: v })} placeholder="Why is the patient being admitted?"/>
              </div>
              <div>
                <label className="text-xs label-tiny">Referring Doctor</label>
                <VoiceInput as="input" testid="ipd-refdoc" className="mt-0" value={form.referring_doctor} onChange={(v) => setForm({ ...form, referring_doctor: v })}/>
              </div>
              <div>
                <label className="text-xs label-tiny">TPA / Insurer</label>
                <VoiceInput as="input" testid="ipd-tpa" className="mt-0" value={form.tpa_insurer} onChange={(v) => setForm({ ...form, tpa_insurer: v })}/>
              </div>
              <div className="col-span-2">
                <label className="text-xs label-tiny">TPA Policy #</label>
                <VoiceInput as="input" className="mt-0" value={form.tpa_policy_no} onChange={(v) => setForm({ ...form, tpa_policy_no: v })}/>
              </div>
              <div>
                <label className="text-xs label-tiny">Attendant / Relative</label>
                <VoiceInput as="input" testid="ipd-attname" className="mt-0" value={form.attendant_name} onChange={(v) => setForm({ ...form, attendant_name: v })}/>
              </div>
              <div>
                <label className="text-xs label-tiny">Relationship</label>
                <VoiceInput as="input" className="mt-0" value={form.attendant_relation} onChange={(v) => setForm({ ...form, attendant_relation: v })} placeholder="Son / Wife / …"/>
              </div>
              <div className="col-span-2">
                <label className="text-xs label-tiny">Attendant Mobile</label>
                <VoiceInput as="input" testid="ipd-attmob" className="mt-0" numeric maxLength={10} value={form.attendant_mobile} onChange={(v) => setForm({ ...form, attendant_mobile: v })}/>
              </div>
            </div>
          </div>
          <DialogFooter><Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button><Button onClick={create} data-testid="ipd-create" className="bg-blue-700 hover:bg-blue-800">Admit Patient</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
